<template>
  <div class="modern-table-page">
    <!-- Search and Filters Section -->
    <div class="filters-section">
      <div class="search-container">
        <label class="search-label">{{ $t("globals.type_to_search") }}</label>
        <b-form-input
          v-model="filter"
          class="search-input"
          :placeholder="$t('globals.search_for') + '...'"
        />
      </div>

      <div class="filter-group">
      </div>

      <b-button
        variant="warning"
        class="search-btn"
        :disabled="!filter"
        @click="filter = ''"
      >
        {{ $t("globals.clear").toUpperCase() }}
      </b-button>
    </div>

    <!-- Table -->
    <div class="table-wrapper">
      <table class="modern-table">
        <thead>
          <tr>
            <th
              v-for="field in fields"
              :key="field.key || field"
              class="sortable"
              @click="handleSort(field.key || field)"
            >
              {{ field.label || field }}
              <span v-if="sortBy === (field.key || field)" class="sort-icon">
                {{ sortDesc ? '▼' : '▲' }}
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          <template v-for="item in paginatedItems">
            <tr
              :key="item.id"
              class="table-row"
              @click="rowClicked(item)"
              :class="{ 'row-expanded': item._showDetails }"
            >
              <td>{{ item.id }}</td>
              <td>{{ item.customer ? item.customer.company_name : '-' }}</td>
              <td>
                <span class="status-badge" :class="`status-${item.status}`">
                  {{ item.status }}
                </span>
              </td>
              <td>{{ item.payment_method || '-' }}</td>
              <td>{{ item.invoice_type || '-' }}</td>
              <td>{{ item.invoice_no || '-' }}</td>
              <td v-b-tooltip.hover :title="item.created_at">
                {{ item.created_at ? item.created_at.substring(0, 10) : '-' }}
              </td>
            </tr>
            <tr v-if="item._showDetails" :key="`${item.id}-details`" class="details-row">
              <td :colspan="fields.length" class="details-cell">
                <div class="details-content">
                  <div class="details-audits">
                    <h6>{{ $t("globals.audits") || "Audits" }}</h6>
                    <div v-for="(audit, idx) in item.audits" :key="idx" class="audit-item">
                      <span v-b-tooltip.hover :title="descriptionForAction(audit.action)">
                        <strong>{{ audit.action }}</strong> - {{ audit.created_at }}
                      </span>
                    </div>
                  </div>
                  <div class="details-actions">
                    <b-button
                      v-if="getUserBrandPermissions.super_admin && item.mollie_payment_id != null"
                      size="sm"
                      variant="info"
                      @click.stop="openMolliePayment(item.mollie_payment_id)"
                    >
                      MolliePayment
                    </b-button>
                    <b-button
                      v-if="item.customer"
                      size="sm"
                      variant="info"
                      @click.stop="$router.push({ name: 'customer', params: { id: item.customer.id } })"
                    >
                      {{ item.customer.company_name }}
                    </b-button>
                  </div>
                </div>
              </td>
            </tr>
          </template>
        </tbody>
      </table>

      <div v-if="filteredItems.length === 0" class="empty-state">
        <p>{{ $t("globals.no_results") }}</p>
      </div>
    </div>

    <!-- Pagination -->
    <div class="pagination-section">
      <div class="controls-left">
        <span class="summary-label">{{ $t("globals.show") }}</span>
        <b-form-select
          v-model="perPage"
          :options="perPageOptions"
          class="per-page-select"
        />
        <span class="pagination-info">
          {{ paginationText }}
        </span>
      </div>
      <div class="pagination-controls">
        <button
          class="page-btn"
          :disabled="currentPage === 1"
          @click="currentPage--"
        >
          &lt;
        </button>
        <button
          v-for="page in visiblePages"
          :key="page"
          class="page-btn"
          :class="{ active: page === currentPage }"
          @click="currentPage = page"
        >
          {{ page }}
        </button>
        <button
          class="page-btn"
          :disabled="currentPage === totalPages"
          @click="currentPage++"
        >
          &gt;
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { currencyFormat } from "@/helpers/price";
import { mapGetters, mapActions, mapMutations } from "vuex";

export default {
  data() {
    return {
      molliePayments: [],
      filter: null,
      currentPage: 1,
      perPage: 20,
      perPageOptions: [
        { text: "20", value: 20 },
        { text: "40", value: 40 },
        { text: "60", value: 60 }
      ],
      sortBy: "created_at",
      sortDesc: true,
      fields: [
        "id",
        {
          key: "customer.company_name",
          label: this.$t("globals.customer"),
          sortable: true
        },
        {
          key: "status",
          label: this.$t("globals.status"),
          sortable: true
        },
        {
          key: "payment_method",
          label: this.$t("globals.payment_method"),
          sortable: true
        },
        {
          key: "invoice_type",
          label: this.$t("globals.invoice_type"),
          sortable: true
        },
        {
          key: "invoice_no",
          label: this.$t("globals.invoice_no"),
          sortable: true
        },
        {
          key: "created_at",
          label: this.$t("globals.created_at"),
          sortable: true
        }
      ]
    };
  },
  methods: {
    ...mapMutations(["setShowLoadingModal"]),
    ...mapActions("axios_mod", ["simpleFetch"]),
    currencyFormat,
    handleSort(fieldKey) {
      if (this.sortBy === fieldKey) {
        this.sortDesc = !this.sortDesc;
      } else {
        this.sortBy = fieldKey;
        this.sortDesc = false;
      }
    },
    rowClicked(item) {
      item._showDetails = !item._showDetails;
      this.molliePayments.forEach(payment => {
        if (item.id != payment.id) {
          payment._showDetails = false;
        }
      });
    },
    fetchMolliePayments() {
      let data = {
        query: `
          query {
            allMolliePayments {
              id
              mollie_payment_id
              status
              payment_method
              invoice_type
              invoice_no
              invoice_id
              created_at
              _showDetails
              _rowVariant
              customer {
                id
                company_name
              }
              audits {
                action
                created_at
              }
            }
          }
        `
      };

      this.setShowLoadingModal(true);
      this.simpleFetch({data, this_ref: this}).then((res) => {
        if (res) {
          this.molliePayments = res.allMolliePayments;
        }
      }).finally(()=>{
        this.setShowLoadingModal(false)
      });
    },
    openMolliePayment(mollie_payment_id) {
      window.open(
        `https://www.mollie.com/dashboard/org_7462221/payments/${mollie_payment_id}`,
        "_blank"
      );
    },
    descriptionForAction(action) {
      let desc = "";
      switch (action) {
        case "created":
          desc = "The payment was created locally"
          break;
        case "open":
          desc = "The payment was created on Mollie and is awaiting actions"
          break;
        case "paid":
          desc = "The payment was paid"
          break;
        case "canceled":
          desc = "The payment was canceled by the customer"
          break;
        case "expired":
          desc = "The customer didnt set up mollie in time"
          break;
      }
      return desc
    }
  },
  computed: {
    ...mapGetters(["getBackendUrl", "getLocale", "getUserBrandPermissions"]),

    filteredItems() {
      let items = [...this.molliePayments];

      if (this.filter) {
        const searchTerm = this.filter.toLowerCase();
        items = items.filter(item => {
          const customer = item.customer?.company_name?.toLowerCase() || '';
          const status = item.status?.toLowerCase() || '';
          const invoiceNo = item.invoice_no?.toLowerCase() || '';
          const paymentMethod = item.payment_method?.toLowerCase() || '';
          return customer.includes(searchTerm) ||
                 status.includes(searchTerm) ||
                 invoiceNo.includes(searchTerm) ||
                 paymentMethod.includes(searchTerm);
        });
      }

      items.sort((a, b) => {
        let aVal, bVal;

        if (this.sortBy === 'customer.company_name') {
          aVal = a.customer?.company_name || '';
          bVal = b.customer?.company_name || '';
        } else {
          aVal = a[this.sortBy] || '';
          bVal = b[this.sortBy] || '';
        }

        if (aVal < bVal) return this.sortDesc ? 1 : -1;
        if (aVal > bVal) return this.sortDesc ? -1 : 1;
        return 0;
      });

      return items;
    },

    paginatedItems() {
      const start = (this.currentPage - 1) * this.perPage;
      const end = start + this.perPage;
      return this.filteredItems.slice(start, end);
    },

    totalPages() {
      return Math.ceil(this.filteredItems.length / this.perPage);
    },

    visiblePages() {
      const pages = [];
      const total = this.totalPages;
      const current = this.currentPage;

      if (total <= 7) {
        for (let i = 1; i <= total; i++) {
          pages.push(i);
        }
      } else {
        if (current <= 4) {
          for (let i = 1; i <= 5; i++) pages.push(i);
          pages.push('...');
          pages.push(total);
        } else if (current >= total - 3) {
          pages.push(1);
          pages.push('...');
          for (let i = total - 4; i <= total; i++) pages.push(i);
        } else {
          pages.push(1);
          pages.push('...');
          for (let i = current - 1; i <= current + 1; i++) pages.push(i);
          pages.push('...');
          pages.push(total);
        }
      }

      return pages;
    },

    paginationText() {
      const start = (this.currentPage - 1) * this.perPage + 1;
      const end = Math.min(this.currentPage * this.perPage, this.filteredItems.length);
      const total = this.filteredItems.length;
      return `${start}-${end} of ${total}`;
    }
  },
  watch: {
    paginatedItems() {
      // Reset to page 1 when filter changes
      if (this.currentPage > this.totalPages) {
        this.currentPage = 1;
      }
    }
  },
  mounted() {
    this.fetchMolliePayments();
  }
};
</script>

<style scoped>
.details-row {
  background-color: #f8f9fa;
}

.details-cell {
  padding: 1.5rem !important;
}

.details-content {
  display: flex;
  gap: 2rem;
  justify-content: space-between;
}

.details-audits {
  flex: 1;
}

.details-audits h6 {
  margin-bottom: 1rem;
  color: #495057;
  font-weight: 600;
}

.audit-item {
  padding: 0.5rem 0;
  border-bottom: 1px solid #e0e0e0;
  font-size: 0.875rem;
}

.audit-item:last-child {
  border-bottom: none;
}

.details-actions {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: flex-end;
}

.row-expanded {
  background-color: #e9ecef;
}
</style>
